/**
 * SitecoreCDP integration module.
 *
 * This module contains React components responsible for initializing and
 * interacting with the Sitecore CDP (Boxever) Engage SDK on the client.
 * It exposes a single default wrapper component which decides whether to
 * initialize the authenticated or unauthenticated CDP client and send events
 * based on the Sitecore page state and the current next-auth session.
 *
 * Key behaviors:
 * - Initializes the CDP client script when a page is in the `normal` state.
 * - Sends a view event for the current page.
 * - When a user is authenticated, sends an identity-by-email event (once per
 *   identity or when the cookie changes) and stores a short-lived cookie to
 *   avoid duplicate identity submissions.
 */
import {
  getCookie,
  initClientScript,
  sendIdentityByEmailEvent,
  sendViewEvent,
  setCookie,
} from 'lib/sitecore/sitecore-cdp';
import { useSession } from 'next-auth/react';
import { useEffect } from 'react';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { ExtendedSitecoreContext } from 'lib/sitecore/sitecore-context';
import { getHostName } from 'lib/sitecore/utils';
import { env } from 'lib/env';

/**
 * Authenticated CDP client initializer and event sender.
 *
 * This component is used when a user is authenticated and a full identity is
 * available from `next-auth`. It initializes the Engage SDK and will send
 * both identity and view events when appropriate.
 *
 * Important internal fields:
 * @param session - NextAuth session object obtained via `useSession()`.
 * @param cdpClientKey - CDP client key (prefers env var `CDP_CLIENT_KEY`).
 * @param cdpApiToken - CDP API token (prefers env var `CDP_API_TOKEN`).
 * @param cdpTarget - CDP API target URL (prefers env var `CDP_TARGET`).
 * @param cdpDomainCookie - The domain used for CDP cookies, resolved from
 *   PUBLIC_URL using `getHostName`.
 * @param cdpPointOfSale - Point-of-sale identifier (prefers env var
 *   `CDP_POINT_OF_SALE`).
 * @param contextItem - Sitecore context from `useSitecoreContext()`.
 * @param PageUrl - Resolved Sitecore item path, used as the view identifier.
 * @param email - Authenticated user's email address.
 * @param pageState - Sitecore page state (only operate when 'normal').
 * @param firstName - User's first name extracted from session name.
 * @param lastName - User's last name extracted from session name.
 *
 * Side effects:
 * - Calls `initClientScript` to boot the Engage SDK when `pageState === 'normal'`.
 * - When identity info is available, calls `sendIdentityByEmailEvent` if the
 *   identity cookie doesn't match, and writes a short-lived cookie to avoid
 *   repeated identity submissions.
 * - Always calls `sendViewEvent` for the current `PageUrl` once available.
 *
 * @returns Empty fragment; this component purely performs client-side side effects.
 */
const SCAuthcdp = (): JSX.Element => {
  // Fail out if we don't have any fields
  const { data: session } = useSession();
  /** CDP client key used to initialize the client script. Falls back to a dev key. */
  const cdpClientKey = env.CDP_CLIENT_KEY;
  /** CDP API token for server-side identity events. */
  const cdpApiToken = env.CDP_API_TOKEN;
  /** CDP API target endpoint. */
  const cdpTarget = env.CDP_TARGET;
  /** Domain used for the CDP cookie (derived from PUBLIC_URL). */
  const cdpDomainCookie = getHostName(env.PUBLIC_URL);
  /** Point-of-sale identifier for the CDP events. */
  const cdpPointOfSale = env.CDP_POINT_OF_SALE;
  const sitecoreContext = useSitecoreContext();
  const contextItem = sitecoreContext.sitecoreContext as ExtendedSitecoreContext;
  /** The Sitecore item path (used as page identifier for view events). */
  const PageUrl = contextItem.itemPath || '';
  /** Authenticated user's email (empty string when not available). */
  const email = session?.user?.email || '';
  const pageState = contextItem.pageState || '';
  const fullName = session?.user?.name || '';
  /** Extracted first name (substring before the first space). */
  const firstName = fullName.substring(0, fullName.indexOf(' '));
  /** Extracted last name (substring after the first space). */
  const lastName = fullName.substring(fullName.indexOf(' ') + 1);
  let authData = '';
  let identityDataLoaded = false as boolean;
  if (session?.authz_site) {
    // authz_site is expected to be an array of authorizations; join into CSV
    authData = session?.authz_site.join();
  }
  if (email.length !== 0 && authData.length !== 0 && PageUrl.length !== 0) {
    identityDataLoaded = true;
  }
  /**
   * Initialize the client-side Engage SDK.
   * This runs on every render but is guarded by `pageState` so it only
   * actually calls `initClientScript` when suitable.
   */
  useEffect(() => {
    if (pageState === 'normal') {
      initClientScript(
        cdpClientKey,
        cdpDomainCookie,
        cdpTarget,
        '1.4.9',
        cdpPointOfSale,
        'https://d35vb5cccm4xzp.cloudfront.net',
        { LogEvents: true, Currency: 'USD', Language: 'EN', Channel: 'WEB' }
      );
    }
  });

  /**
   * Send identity (email) to the CDP when user identity is available.
   * This effect is triggered when `identityDataLoaded` becomes true and will
   * compare the stored identity cookie to avoid duplicate submissions.
   */
  useEffect(() => {
    if (identityDataLoaded && pageState === 'normal') {
      if (getCookie('cdpi') !== email) {
        sendIdentityByEmailEvent(
          PageUrl,
          email,
          cdpClientKey,
          cdpApiToken,
          '',
          firstName,
          authData,
          lastName
        );
      }
      setCookie('cdpi', email, 19); // setting 19 mins for cookie to expire
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityDataLoaded]);
  /**
   * Send a view event for the current page path when it becomes available.
   */
  useEffect(() => {
    if (PageUrl.length !== 0 && pageState === 'normal') {
      sendViewEvent(PageUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PageUrl]);
  return <></>;
};
/**
 * Unauthenticated CDP client initializer.
 *
 * This component initializes the Engage SDK and sends only view events.
 * It is intended for pages where no authenticated identity is present.
 *
 * Fields:
 * - cdpClientKey: client key used to bootstrap the client script.
 * - cdpTarget: API target endpoint.
 * - cdpDomainCookie: cookie domain derived from PUBLIC_URL.
 * - cdpPointOfSale: point-of-sale identifier.
 * - PageUrl: current Sitecore item path used as the view identifier.
 * - pageState: Sitecore page state guard.
 *
 * Returns an empty fragment; side effects only.
 */
const SCcdp = (): JSX.Element => {
  // Fail out if we don't have any fields
  const cdpClientKey = process.env.CDP_CLIENT_KEY || 'nmduatawv6g974ig49qf7suh5wafcvl7';
  /** CDP API target for view events. */
  const cdpTarget = process.env.CDP_TARGET || 'https://api-us.boxever.com/v1.2';
  /** Domain used for CDP cookies. */
  const cdpDomainCookie = getHostName(process.env.PUBLIC_URL || 'http://localhost:3000');
  /** Point-of-sale identifier for events. */
  const cdpPointOfSale = process.env.CDP_POINT_OF_SALE || 'BeConnected-local';
  const sitecoreContext = useSitecoreContext();
  const contextItem = sitecoreContext.sitecoreContext as ExtendedSitecoreContext;
  const PageUrl = contextItem.itemPath || '';
  const pageState = contextItem.pageState || '';

  /** Initialize the client SDK when the page is in normal state. */
  useEffect(() => {
    if (pageState === 'normal') {
      initClientScript(
        cdpClientKey,
        cdpDomainCookie,
        cdpTarget,
        '1.4.9',
        cdpPointOfSale,
        'https://d35vb5cccm4xzp.cloudfront.net',
        { LogEvents: true, Currency: 'USD', Language: 'EN', Channel: 'WEB' }
      );
    }
  });
  /** Send a view event for the current page path when available. */
  useEffect(() => {
    if (PageUrl.length !== 0 && pageState === 'normal') {
      sendViewEvent(PageUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PageUrl]);
  return <></>;
};

/**
 * Wrapper component that decides which CDP initializer to mount.
 *
 * Behavior:
 * - If the feature flag `FEATURE_FLAG_ENABLE_ENGAGE_SDK` is enabled (true),
 *   the wrapper returns `null` to opt-out of any client initialization.
 * - Reads `SKIP_AUTHENTICATION` env var to decide between the authenticated
 *   or unauthenticated initializer when `pageState === 'normal'`.
 *
 * Note about hooks: this file conditionally renders components that call hooks.
 * The condition is static at build time and therefore safe in practice, and
 * the code comments intentionally call this out to explain the deviation from
 * the usual rules of hooks.
 *
 * @returns Either an instance of `SCcdp`, `SCAuthcdp`, or `null` - purely for
 * side-effect execution; nothing is rendered to the DOM.
 */
const SCcdpWrapper: React.FC = () => {
  // NOTE: While this **TECHNICALLY** breaks the rules of hooks by introducing a conditional
  // call to useSitecoreContext, in this case it's safe because the flag is static and can
  // only be set at build time.  This means it will never change between renders, so the
  // behavior is consistent.
  if (env.FEATURE_FLAG_ENABLE_ENGAGE_SDK === true) {
    return <></>;
  }
  /**
   * When true, the app will initialize the CDP without attempting to send
   * authenticated identity events. Controlled by env var `SKIP_AUTHENTICATION`.
   */
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const skipAuthentication = process.env.SKIP_AUTHENTICATION === 'true' ? true : false;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const sitecoreContext = useSitecoreContext();
  const pageState = sitecoreContext.sitecoreContext.pageState;
  // Don't use when in Sitecore since it'll fail
  if (pageState === 'normal') {
    return skipAuthentication ? <SCcdp /> : <SCAuthcdp />;
  }
  return <></>;
};

export default SCcdpWrapper;
